import {
  type FilterFunction,
  type FilterType,
  FilterTypes,
  type FilterValues,
  createDateRangeFilterValidators,
  filterToPostGraphileFilter,
  transformRange,
} from '@axinom/mosaic-ui';
import {
  ResourceStatus,
  type ServiceDeploymentFilter,
  type ServiceDeploymentManifestFilter,
} from '../../../../generated/hosting-service';
import {
  type CustomizableServiceDeploymentData,
  DeploymentStatusConstants,
} from './CustomizableServiceDeploymentCreate.types';

export const getEnumLabel = (input: string): string => {
  return DeploymentStatusConstants[input] ?? input;
};

const [createFromDateFilterValidator, createToDateFilterValidator] =
  createDateRangeFilterValidators<CustomizableServiceDeploymentData>();

export function useServiceDeploymentFilters(): {
  readonly filterOptions: FilterType<CustomizableServiceDeploymentData>[];
  readonly transformFilters: (
    filters: FilterValues<CustomizableServiceDeploymentData>,
  ) => ServiceDeploymentFilter | undefined;
  readonly defaultFilterValues: FilterValues<CustomizableServiceDeploymentData>;
} {
  const filterOptionsForResourceStatus: (keyof typeof ResourceStatus)[] = [
    'Ready',
    'DeleteCompleted',
    'CreatePending',
    'DeletePending',
    'CreateError',
    'DeleteError',
  ];

  const filterOptions: FilterType<CustomizableServiceDeploymentData>[] = [
    {
      label: 'Name',
      property: 'name',
      type: FilterTypes.FreeText,
    },
    {
      label: 'Container Image',
      property: 'containerImageVersion',
      type: FilterTypes.FreeText,
    },
    {
      label: 'Deployment Manifest',
      property: 'serviceDeploymentManifest',
      type: FilterTypes.FreeText,
    },
    {
      label: 'Pilet Artifact(s)',
      property: 'servicePiletArtifacts',
      type: FilterTypes.FreeText,
    },
    {
      label: 'Status',
      property: 'status',
      type: FilterTypes.Options,
      options: filterOptionsForResourceStatus.map((key) => ({
        value: { ...ResourceStatus, type: 'enum' }[key],
        label: getEnumLabel({ ...ResourceStatus, type: 'enum' }[key] ?? ''),
      })),
    },
    {
      label: 'Active Deployment',
      property: 'active',
      type: FilterTypes.Options,
      options: [
        {
          label: 'Yes',
          value: true,
        },
        {
          label: 'No',
          value: false,
        },
      ],
    },
    {
      label: 'Creation Period (From)',
      property: 'createdDate',
      type: FilterTypes.Date,
      onValidate: createFromDateFilterValidator('createdDate'),
    },
    {
      label: 'Creation Period (To)',
      property: 'createdDate',
      type: FilterTypes.Date,
      onValidate: createToDateFilterValidator('createdDate'),
    },
    {
      label: 'Modification Period (From)',
      property: 'updatedDate',
      type: FilterTypes.Date,
      onValidate: createFromDateFilterValidator('updatedDate'),
    },
    {
      label: 'Modification Period (To)',
      property: 'updatedDate',
      type: FilterTypes.Date,
      onValidate: createToDateFilterValidator('updatedDate'),
    },
  ];

  const transformFilters = (
    filters: FilterValues<CustomizableServiceDeploymentData>,
  ): ServiceDeploymentFilter | undefined => {
    const deploymentManifestNameFilter: FilterFunction = (
      val,
    ): ServiceDeploymentManifestFilter => {
      if (typeof val === 'string') {
        return {
          name: { includesInsensitive: val },
        };
      } else {
        return {};
      }
    };

    return filterToPostGraphileFilter<ServiceDeploymentFilter>(filters, {
      name: 'includesInsensitive',
      containerImageVersion: 'includesInsensitive',
      servicePiletArtifacts: 'includesInsensitive',
      status: 'equalTo',
      active: 'equalTo',
      createdDate: transformRange,
      updatedDate: transformRange,
      serviceDeploymentManifest: deploymentManifestNameFilter,
    });
  };

  const defaultFilterValues: FilterValues<CustomizableServiceDeploymentData> = {
    active: true,
  };

  return { filterOptions, transformFilters, defaultFilterValues };
}
